import { ALL_SECTIONS } from '../../components/layout/menyuAdminki';
import { captureVisibleCanvas } from '../stol/snapshot';

/**
 * Снимок экрана к сообщению о проблеме (`s737`). Снимается страница панели, а
 * не рабочий стол: общий механизм «Сессии» отдаёт холст видимой области, а здесь
 * на нём закрываются телефоны клиентов и обводится место, которое показал человек.
 */

export interface Snimok {
  canvas: HTMLCanvasElement;
  /** Сколько телефонных номеров закрыто на снимке. */
  skryto: number;
}

export interface Metka { x: number; y: number; w: number; h: number }

// +7 / 8 и десять цифр с любыми скобками, пробелами и дефисами между ними.
const NOMER = /(?:\+7|\b8)[\s\u00A0(-]*\d{3}[\s\u00A0)-]*\d{3}[\s\u00A0-]*\d{2}[\s\u00A0-]*\d{2}\b/g;

export async function snyatEkran(): Promise<Snimok | null> {
  const wrapper = document.getElementById('stol-screen-root');
  if (!wrapper) return null;
  // ⛔ Номера закрываются НА СТРАНИЦЕ на время съёмки, а не закраской поверх готового
  // снимка: копия страницы, с которой делается снимок, сбрасывает прокрутку вложенных
  // списков, и закраска «по месту на экране» легла бы мимо номера.
  const { vernut, skryto } = skrytNomera(wrapper);
  try {
    const canvas = await captureVisibleCanvas();
    return canvas ? { canvas, skryto } : null;
  } finally {
    vernut();
  }
}

export function skrytNomera(wrapper: HTMLElement): { vernut: () => void; skryto: number } {
  const teksty: { node: Node; bylo: string; stalo: string }[] = [];
  const polya: { input: HTMLInputElement; color: string }[] = [];
  let skryto = 0;

  const walker = document.createTreeWalker(wrapper, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const bylo = node.nodeValue ?? '';
    if (bylo.length < 10) continue;
    NOMER.lastIndex = 0;
    if (!NOMER.test(bylo)) continue;
    NOMER.lastIndex = 0;
    const stalo = bylo.replace(NOMER, (nomer) => { skryto += 1; return nomer.replace(/\d/g, '•'); });
    teksty.push({ node, bylo, stalo });
  }
  // Номер в поле ввода — не текстовый узел: на время съёмки текст поля делается невидимым.
  wrapper.querySelectorAll<HTMLInputElement>('input').forEach((input) => {
    NOMER.lastIndex = 0;
    if (NOMER.test(input.value)) {
      polya.push({ input, color: input.style.color });
      skryto += 1;
    }
  });

  teksty.forEach((t) => { t.node.nodeValue = t.stalo; });
  polya.forEach((p) => { p.input.style.color = 'transparent'; });

  return {
    skryto,
    vernut: () => {
      // Возвращаем только то, что страница не успела переписать сама.
      teksty.forEach((t) => { if (t.node.nodeValue === t.stalo) t.node.nodeValue = t.bylo; });
      polya.forEach((p) => { p.input.style.color = p.color; });
    },
  };
}

/** Снимок для отправки: с обведённым местом, JPEG — чтобы не упереться в предел размера. */
export function vKartinku(snimok: Snimok, metka: Metka | null): string {
  const out = document.createElement('canvas');
  out.width = snimok.canvas.width;
  out.height = snimok.canvas.height;
  const ctx = out.getContext('2d');
  if (!ctx) return snimok.canvas.toDataURL('image/jpeg', 0.82);
  ctx.drawImage(snimok.canvas, 0, 0);
  if (metka) {
    const x = metka.x * out.width;
    const y = metka.y * out.height;
    const w = metka.w * out.width;
    const h = metka.h * out.height;
    ctx.fillStyle = 'rgba(229,72,77,.10)';
    ctx.fillRect(x, y, w, h);
    ctx.lineWidth = 3;
    ctx.strokeStyle = '#E5484D';
    ctx.strokeRect(x, y, w, h);
  }
  return out.toDataURL('image/jpeg', 0.82);
}

/** Имя экрана человеческими словами — по меню панели; чего в меню нет, называется адресом. */
export function imyaEkrana(pathname: string): string {
  let luchshiy: { label: string; dlina: number } | null = null;
  ALL_SECTIONS.forEach((section) => {
    const punkty = [{ to: section.to, label: section.label }, ...(section.children ?? []).map((c) => ({ to: c.to, label: `${section.label} · ${c.label}` }))];
    punkty.forEach((p) => {
      const sovpal = pathname === p.to || pathname.startsWith(`${p.to}/`);
      if (sovpal && (!luchshiy || p.to.length > luchshiy.dlina)) {
        luchshiy = { label: pathname === p.to ? p.label : `${p.label} · карточка`, dlina: p.to.length };
      }
    });
  });
  return (luchshiy as { label: string } | null)?.label ?? pathname;
}

/** Версия панели — имя собранного файла приложения: по нему сборка находится однозначно. */
export function versiyaPaneli(): string | null {
  const script = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]');
  const m = script?.src.match(/\/assets\/([^/?#]+)\.js/);
  return m ? m[1].slice(0, 64) : null;
}
